import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, type ApiError, type GetTargetGridResult } from '@whinerp/client';
import { Button, EmptyState, ErrorState, Num, Panel, SectionLabel, Skeleton } from '@whinerp/ui';
import { formatCount, formatMoney } from '@whinerp/contracts/format';
import { useFormat, useT } from '../../app/i18n.js';

/**
 * Targets and attainment (F-053).
 *
 * The grid is the feature, and everything around it is there because a number
 * in a cell is read as the whole truth unless the page says otherwise.
 *
 * Three notices sit above the grid and each one names a scope the figures do
 * NOT cover: money closed by somebody who is not a row here, money no exchange
 * rate could bring into this currency, and teams that share people and
 * therefore do not add up down the column. Each is drawn only when it applies,
 * because a permanent caveat is one nobody reads.
 *
 * Targets are typed in place and saved when the cell is left. Paste exists
 * beside it rather than instead of it - a quarter is set once from a
 * spreadsheet and corrected one cell at a time for the rest of it.
 */

type Grid = GetTargetGridResult;
type Row = Grid['rows'][number];
type Cell = Row['cells'][number];

const THIS_YEAR = new Date().getUTCFullYear();
/** Enough to set next year and look back at the last two. */
const YEARS = [THIS_YEAR + 1, THIS_YEAR, THIS_YEAR - 1, THIS_YEAR - 2];
const MONTHS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];

export function TargetsPanel(): JSX.Element {
  const t = useT();
  const format = useFormat();
  const queryClient = useQueryClient();
  const [fiscalYear, setFiscalYear] = useState(THIS_YEAR);
  const [periodKind, setPeriodKind] = useState<'month' | 'quarter' | 'year'>('quarter');
  const [subjectKind, setSubjectKind] = useState<'user' | 'team'>('user');
  /*
   * What a target TYPED HERE means. It is a property of the grid rather than
   * of each cell because nobody sets a money target and a deal target for the
   * same person in the same quarter - but a cell renders whatever measure its
   * own stored target has, so switching this never relabels a number somebody
   * already agreed.
   */
  const [measure, setMeasure] = useState<'amount' | 'count'>('amount');
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [paste, setPaste] = useState('');
  const [pasteRefusal, setPasteRefusal] = useState<string | null>(null);
  const [pasteApplied, setPasteApplied] = useState<number | null>(null);

  const grid = useQuery<Grid, ApiError>({
    queryKey: ['targets', fiscalYear, periodKind, subjectKind],
    queryFn: () => api.getTargetGrid({ query: { fiscalYear, periodKind, subjectKind } }),
  });

  const refresh = (): Promise<unknown> => queryClient.invalidateQueries({ queryKey: ['targets'] });

  const save = useMutation<unknown, ApiError, Parameters<typeof api.setTargets>[0]['body']['entries']>({
    mutationFn: (entries) => api.setTargets({ body: { entries } }),
    /*
     * Only the cells this write covered are cleared, never all of them.
     * Clearing the whole draft map discarded whatever had been typed while the
     * request was in flight - somebody moving along a row loses the cell they
     * are in the moment the previous one lands, and the cell simply reverts
     * with nothing said.
     */
    onSuccess: async (_result, entries) => {
      const written = new Set(entries.map((entry) => `${entry.subjectId}|${entry.periodStart}`));
      setDrafts((current) => {
        const next: Record<string, string> = {};
        for (const [key, value] of Object.entries(current)) if (!written.has(key)) next[key] = value;
        return next;
      });
      await refresh();
    },
  });

  const remove = useMutation<
    unknown,
    ApiError,
    { subjectKind: 'user' | 'team'; subjectId: string; periodStart: string; periodEnd: string }
  >({
    mutationFn: (where) => api.deleteTarget({ query: where }),
    onSuccess: () => refresh(),
  });

  const fiscalStart = useMutation<unknown, ApiError, number>({
    mutationFn: (month) => api.setFiscalYearStart({ body: { month } }),
    onSuccess: () => refresh(),
  });

  const periodLabel = (period: Grid['periods'][number]): string =>
    period.index === null
      ? t('target.period.custom', { values: { start: period.start, end: period.end } })
      : t(`target.period.${period.kind}` as 'target.period.quarter', {
          values: { index: formatCount(period.index, format) },
        });

  const byName = useMemo(() => {
    const map = new Map<string, Row>();
    for (const row of grid.data?.rows ?? []) map.set(row.subject.name.trim().toLowerCase(), row);
    return map;
  }, [grid.data]);

  if (grid.isPending) return <Skeleton className="h-block-lg w-full" />;
  if (grid.isError) {
    return (
      <ErrorState title={t('state.error')} body={grid.error.message} onRetry={() => void grid.refetch()} />
    );
  }

  const data = grid.data;
  const money = (amount: string): string => formatMoney(amount, format, { currency: data.currency });
  const cellKey = (row: Row, cell: Cell): string => `${row.subject.id}|${cell.periodStart}`;
  /** The column a cell is in, by name. An input labelled only "target" in a
      grid of twelve is an input a screen reader cannot place. */
  const columnLabel = (periodStart: string): string => {
    const period = data.periods.find((entry) => entry.start === periodStart);
    return period ? periodLabel(period) : periodStart;
  };

  const commit = (row: Row, cell: Cell): void => {
    const key = cellKey(row, cell);
    const draft = drafts[key];
    if (draft === undefined) return;
    const trimmed = draft.trim();
    if (trimmed === '') {
      /*
       * Emptying a cell REMOVES the target rather than setting it to zero.
       * Zero is a commitment to close nothing and an empty cell is nobody
       * having agreed one, and the two are different afternoons - so the
       * control that clears is the one that deletes.
       *
       * Addressed by subject and period, not by the id in `cell` - which was
       * captured when this row last rendered. Somebody who types a target and
       * clears it again before the grid refetches holds a cell that still says
       * there is nothing to delete, and a clear addressed by that id silently
       * does nothing.
       */
      if (cell.target !== null || cell.targetId !== null || drafts[key] !== undefined) {
        remove.mutate({
          subjectKind: row.subject.kind,
          subjectId: row.subject.id,
          periodStart: cell.periodStart,
          periodEnd: cell.periodEnd,
        });
      }
      setDrafts((current) => {
        const next = { ...current };
        delete next[key];
        return next;
      });
      return;
    }
    save.mutate([
      {
        subjectKind: row.subject.kind,
        subjectId: row.subject.id,
        periodKind: data.periodKind,
        periodStart: cell.periodStart,
        periodEnd: cell.periodEnd,
        measure,
        amount: trimmed,
        currency: measure === 'amount' ? data.currency : null,
      },
    ]);
  };

  /**
   * Turns a pasted block into entries, or refuses the whole block.
   *
   * Nothing is sent until every line matches somebody, because a paste that
   * applied the first eleven rows and stopped would leave a half-set quarter
   * with nothing on the page saying which half.
   */
  const applyPaste = (): void => {
    setPasteApplied(null);
    const lines = paste.split('\n').map((line) => line.trim()).filter((line) => line !== '');
    if (lines.length === 0) {
      setPasteRefusal(t('target.paste.body'));
      return;
    }
    const entries: Parameters<typeof api.setTargets>[0]['body']['entries'] = [];
    for (const line of lines) {
      const parts = line.split('\t').map((part) => part.trim());
      const name = (parts.shift() ?? '').toLowerCase();
      const row = byName.get(name);
      if (!row) {
        setPasteRefusal(`"${line.split('\t')[0] ?? ''}" is not a name on this grid.`);
        return;
      }
      if (parts.length > data.periods.length) {
        setPasteRefusal(
          `That row has ${String(parts.length)} amounts and this grid has ${String(data.periods.length)} periods.`,
        );
        return;
      }
      parts.forEach((amount, index) => {
        const period = data.periods[index];
        if (!period || amount === '') return;
        entries.push({
          subjectKind: row.subject.kind,
          subjectId: row.subject.id,
          periodKind: data.periodKind,
          periodStart: period.start,
          periodEnd: period.end,
          measure,
          amount: amount.replace(/[,\s]/g, ''),
          currency: measure === 'amount' ? data.currency : null,
        });
      });
    }
    if (entries.length === 0) {
      setPasteRefusal(t('target.paste.body'));
      return;
    }
    setPasteRefusal(null);
    save.mutate(entries, {
      onSuccess: () => {
        setPaste('');
        setPasteApplied(entries.length);
      },
    });
  };

  const failure = save.error ?? remove.error ?? fiscalStart.error;
  const empty = data.rows.length === 0;

  return (
    <Panel data-region="targets" id="targets">
      <div className="flex flex-col gap-4">
        <div className="flex flex-col gap-1">
          <SectionLabel>{t('target.title')}</SectionLabel>
          <p className="text-meta text-ink-meta">{t('target.body')}</p>
        </div>

        <div className="flex flex-wrap items-end gap-3">
          <label className="flex flex-col gap-1 text-dense font-medium text-ink">
            {t('target.year')}
            <select
              data-target-year
              className="rounded-control border border-rule bg-surface px-2 py-1 text-body text-ink"
              value={String(fiscalYear)}
              onChange={(event) => setFiscalYear(Number(event.target.value))}
            >
              {YEARS.map((year) => (
                <option key={year} value={String(year)}>
                  {String(year)}
                </option>
              ))}
            </select>
          </label>

          <label className="flex flex-col gap-1 text-dense font-medium text-ink">
            {t('target.by')}
            <select
              data-target-subject
              className="rounded-control border border-rule bg-surface px-2 py-1 text-body text-ink"
              value={subjectKind}
              onChange={(event) => setSubjectKind(event.target.value === 'team' ? 'team' : 'user')}
            >
              <option value="user">{t('target.subject.user')}</option>
              <option value="team">{t('target.subject.team')}</option>
            </select>
          </label>

          <label className="flex flex-col gap-1 text-dense font-medium text-ink">
            {t('target.measure')}
            <select
              data-target-measure
              className="rounded-control border border-rule bg-surface px-2 py-1 text-body text-ink"
              value={measure}
              onChange={(event) => setMeasure(event.target.value === 'count' ? 'count' : 'amount')}
            >
              <option value="amount">{t('target.measure.amount')}</option>
              <option value="count">{t('target.measure.count')}</option>
            </select>
          </label>

          <label className="flex flex-col gap-1 text-dense font-medium text-ink">
            {t('target.periodKind')}
            <select
              data-target-period
              className="rounded-control border border-rule bg-surface px-2 py-1 text-body text-ink"
              value={periodKind}
              onChange={(event) =>
                setPeriodKind(
                  event.target.value === 'month' ? 'month' : event.target.value === 'year' ? 'year' : 'quarter',
                )
              }
            >
              <option value="month">{t('target.periodKind.month')}</option>
              <option value="quarter">{t('target.periodKind.quarter')}</option>
              <option value="year">{t('target.periodKind.year')}</option>
            </select>
          </label>

          <label className="flex flex-col gap-1 text-dense font-medium text-ink">
            {t('target.fiscalStart')}
            <select
              data-target-fiscal-start
              className="rounded-control border border-rule bg-surface px-2 py-1 text-body text-ink"
              value={String(data.fiscalYearStartMonth)}
              onChange={(event) => fiscalStart.mutate(Number(event.target.value))}
            >
              {MONTHS.map((month) => (
                <option key={month} value={String(month)}>
                  {String(month)}
                </option>
              ))}
            </select>
          </label>
        </div>
        <p className="text-meta text-ink-meta">{t('target.fiscalStart.body')}</p>

        {failure ? (
          <p data-target-refusal className="rounded-control bg-danger-tint px-3 py-2 text-dense text-danger-text">
            {failure.message}
          </p>
        ) : null}

        {/* Each of these names something the grid does not cover, and each is
            drawn only when it applies. A caveat that is always there is a
            caveat nobody reads. */}
        {data.unattributed.deals > 0 ? (
          <p data-target-unattributed className="rounded-control bg-warning-tint px-3 py-2 text-dense text-warning-text">
            {t('target.unattributed', {
              values: {
                amount: money(data.unattributed.amount),
                count: formatCount(data.unattributed.deals, format),
              },
            })}
          </p>
        ) : null}
        {data.unconvertible.length > 0 ? (
          <p data-target-unconvertible className="rounded-control bg-warning-tint px-3 py-2 text-dense text-warning-text">
            {t('target.unconvertible', {
              values: { currencies: [...new Set(data.unconvertible.map((gap) => gap.from))].join(', ') },
            })}
          </p>
        ) : null}
        {data.sharedDeals > 0 ? (
          <p data-target-shared className="rounded-control bg-warning-tint px-3 py-2 text-dense text-warning-text">
            {t('target.sharedDeals', { values: { count: formatCount(data.sharedDeals, format) } })}
          </p>
        ) : null}
        {data.overlappingPeople > 0 ? (
          <p data-target-overlapping className="rounded-control bg-warning-tint px-3 py-2 text-dense text-warning-text">
            {t('target.overlapping', { values: { count: formatCount(data.overlappingPeople, format) } })}
          </p>
        ) : null}

        {empty ? (
          <EmptyState
            reason="no-records"
            illustration="Target"
            title={t('target.empty.title')}
            body={t('target.empty.body')}
            /* There are no rows because there is nobody to put on them. The
               next action is not "set a target" - it is to invite the people
               who would be measured by one. */
            primary={{ label: t('setup.category.people'), href: '/setup#people', icon: 'Users' }}
          />
        ) : (
          <div data-scroll-container className="overflow-x-auto">
            <table className="w-full min-w-block-xl border-collapse text-body">
              <thead>
                <tr>
                  <th scope="col" className="border-b border-b-rule py-2 text-start text-micro uppercase tracking-label text-ink-label">
                    {subjectKind === 'user' ? t('target.subject.user') : t('target.subject.team')}
                  </th>
                  {data.periods.map((period) => (
                    <th
                      key={period.start}
                      scope="col"
                      data-target-column={period.start}
                      className="border-b border-b-rule py-2 text-start text-micro uppercase tracking-label text-ink-label"
                    >
                      {periodLabel(period)}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.rows.map((row) => (
                  <tr key={row.subject.id} data-target-row={row.subject.id}>
                    <th scope="row" className="border-b border-b-rule py-2 text-start font-medium text-ink">
                      {row.subject.name}
                    </th>
                    {row.cells.map((cell) => {
                      const key = cellKey(row, cell);
                      return (
                        <td
                          key={cell.periodStart}
                          data-target-cell={key}
                          /* Whether a target has been AGREED, which is a fact
                             from the server and not what is currently typed in
                             the box. The two differ for as long as a save is in
                             flight, and that gap is exactly where a screen
                             looks like it has saved and has not. */
                          data-target-agreed={cell.target === null ? 'no' : 'yes'}
                          className="border-b border-b-rule py-2 align-top"
                        >
                          <div className="flex flex-col gap-1">
                            <input
                              data-target-input={key}
                              aria-label={`${row.subject.name}, ${columnLabel(cell.periodStart)}, ${t('target.column.target')}`}
                              inputMode="decimal"
                              className="w-col-amount rounded-control border border-rule bg-surface px-2 py-1 text-body text-ink"
                              value={drafts[key] ?? cell.target ?? ''}
                              data-target-cell-measure={cell.targetMeasure ?? measure}
                              placeholder={t('target.noTarget')}
                              onChange={(event) =>
                                setDrafts((current) => ({ ...current, [key]: event.target.value }))
                              }
                              onBlur={() => commit(row, cell)}
                            />
                            {/* Closed, always - a cell with a target and no
                                result is half the question. Shown in whatever
                                the cell's own target is counted in, because a
                                count of twenty rendered as twenty dirhams is a
                                label that lies. */}
                            <Num data-target-attained={key} className="text-meta text-ink-meta">
                              {(cell.targetMeasure ?? measure) === 'count'
                                ? t('target.dealsClosed', { values: { count: formatCount(cell.deals, format) } })
                                : money(cell.attained)}
                            </Num>
                            {cell.attainmentPercent === null ? null : (
                              <span data-target-percent={key} className="text-meta text-ink-meta">
                                {t('target.attainment', { values: { percent: cell.attainmentPercent } })}
                              </span>
                            )}
                          </div>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <p className="text-meta text-ink-meta">{t('target.scope')}</p>

        {/* Targets held against spans this year start no longer produces. They
            are somebody's agreed number and this is the only screen that could
            correct them, so they must not vanish from it. */}
        {data.strays.length > 0 ? (
          <div data-target-strays className="flex flex-col gap-1">
            <SectionLabel>{t('target.strays')}</SectionLabel>
            <ul className="flex flex-col">
              {data.strays.map((stray) => (
                <li
                  key={stray.targetId}
                  data-target-stray={stray.targetId}
                  className="flex flex-wrap items-baseline gap-2 border-t border-t-rule py-1 first:border-t-0"
                >
                  <span className="min-w-0 flex-1 truncate text-body text-ink">{stray.subject.name}</span>
                  <span className="text-meta text-ink-meta">
                    {t('target.period.custom', { values: { start: stray.periodStart, end: stray.periodEnd } })}
                  </span>
                  <Num className="text-meta text-ink-meta">{money(stray.amount)}</Num>
                  <Button
                    intent="ghost"
                    size="small"
                    data-target-stray-remove={stray.targetId}
                    onClick={() =>
                      remove.mutate({
                        subjectKind: stray.subject.kind,
                        subjectId: stray.subject.id,
                        periodStart: stray.periodStart,
                        periodEnd: stray.periodEnd,
                      })
                    }
                  >
                    {t('target.remove')}
                  </Button>
                </li>
              ))}
            </ul>
          </div>
        ) : null}

        <div className="flex flex-col gap-2 border-t border-t-rule pt-3">
          <SectionLabel>{t('target.paste.title')}</SectionLabel>
          <p className="text-meta text-ink-meta">{t('target.paste.body')}</p>
          <textarea
            data-target-paste
            aria-label={t('target.paste.title')}
            rows={3}
            className="w-full rounded-control border border-rule bg-surface px-2 py-1 font-mono text-dense text-ink"
            value={paste}
            onChange={(event) => setPaste(event.target.value)}
          />
          {pasteRefusal ? (
            <p data-target-paste-refusal className="rounded-control bg-danger-tint px-3 py-2 text-dense text-danger-text">
              {pasteRefusal}
            </p>
          ) : null}
          {pasteApplied === null ? null : (
            <p data-target-paste-applied className="text-meta text-success-text">
              {t('target.paste.applied', { values: { count: formatCount(pasteApplied, format) } })}
            </p>
          )}
          <div>
            <Button intent="secondary" size="small" data-target-paste-apply onClick={applyPaste}>
              {t('target.paste.apply')}
            </Button>
          </div>
        </div>
      </div>
    </Panel>
  );
}
